import { useState, useCallback } from 'react';
import type { PostgrestError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useApp } from './useApp';
import { extractEnvelope } from '../lib/extractEnvelope';
import type { ToastState } from '../components/ui/Toast';

// ── Local types ─────────────────────────────────────────────────────────────

interface PoBuilderLineItem {
  product_id: string;
  isg_sku: string | null;
  quantity: number;
  cost_per_unit: number | null;
  /**
   * Primary path — kept for backward compatibility with code that hasn't
   * migrated to `allocations`. Treat as the representative path on the
   * po_line_items row. The full split lives in `allocations`.
   */
  fulfillment_path: string | null;
  /**
   * Per-path unit allocation: { fba: 3, fbm: 5, shopify: 10 }. Only paths
   * with quantity > 0 should appear. Persisted as child rows in
   * po_line_item_allocations after the parent line item is inserted.
   */
  allocations?: Record<string, number>;
  est_profit_per_unit?: number | null;
  /**
   * Persisted to po_line_items.profitability_snapshot (jsonb). Callers pass
   * narrower shapes — keep this `unknown` so structural-typing widens cleanly.
   */
  profitability_snapshot?: unknown;
}

interface SubmitPOArgs {
  poNumber: string;
  distributorId: string;
  orderDate: string;
  expectedDeliveryDate?: string | null;
  fulfillmentPath: string;
  notes?: string | null;
  lineItems: PoBuilderLineItem[];
}

// W4b-α — the edit-mode resubmit of an EXISTING PO (no poNumber — the RPC keeps it;
// notes stay a client-side post-RPC call). Mirrors SubmitPOArgs otherwise.
interface ResubmitPOArgs {
  poId: string;
  distributorId: string;
  orderDate: string;
  expectedDeliveryDate?: string | null;
  fulfillmentPath: string;
  lineItems: PoBuilderLineItem[];
}

export interface CreditInfo {
  creditLimit: number;
  creditUsed: number;
  available: number;
}

interface PurchaseOrderInsertResult {
  id: string;
  po_number: string;
  status: string;
}

interface SubmitPOResult {
  success: boolean;
  po?: PurchaseOrderInsertResult;
  error?: string;
}

// supabase.rpc('generate_po_number') has no args and returns text; widen so
// the strict generated rpc signature doesn't reject the no-args call.
type LooseRpc = (
  name: string,
  args?: Record<string, unknown>
) => PromiseLike<{ data: unknown; error: PostgrestError | null }>;
// IMPORTANT: `.bind(supabase)` preserves the `this` binding. Without it, the
// detached `supabase.rpc` reference loses its context and throws at runtime
// with "Cannot read properties of undefined (reading 'rest')" on first call.
const callRpc = supabase.rpc.bind(supabase) as unknown as LooseRpc;

// (insertAllocationsFor removed FIX Session 2 — both submitPO and saveDraft now
//  write allocations inside their atomic RPCs [submit_purchase_order / save_po_draft];
//  the client-side allocation insert is dead.)

/**
 * Safely extract a human-readable error message from any thrown value.
 * Supabase RPC/PostgREST errors are plain objects (not Error instances), so
 * `e instanceof Error ? e.message : String(e)` falls through to "[object
 * Object]" — the BACKLOG-859 toast bug. Prefer .message when the value is an
 * object with a string message field; fall back to String(e) only as a last
 * resort for primitive throws.
 */
function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (e && typeof e === 'object') {
    const m = (e as { message?: unknown }).message;
    if (typeof m === 'string' && m.length > 0) return m;
  }
  return String(e);
}

export interface UsePOBuilderResult {
  submitPO: (args: SubmitPOArgs) => Promise<SubmitPOResult>;
  saveDraft: (args: SubmitPOArgs) => Promise<SubmitPOResult>;
  /**
   * W4b-α — atomic edit-mode resubmit (ADR-54 amendment). One call to
   * resubmit_purchase_order (gate-first §4 PO.UNREVIEWED_MATCH + from-state
   * {draft,rejected} PO.NOT_EDITABLE + delete-reinsert lines + nested allocations,
   * all in one txn) — replaces POBuilder's 4 non-atomic edit-mode PostgREST calls.
   * Surfaces typed errors via `error` (the same path submitPO uses).
   */
  resubmitPO: (args: ResubmitPOArgs) => Promise<SubmitPOResult>;
  generatePONumber: () => Promise<string | null>;
  getCreditInfo: (distributorId: string) => Promise<CreditInfo | null>;
  /**
   * W4a (Phase Wellspring) — the PO-submit verification-gate ack (Moment 2).
   * Calls mark_match_reviewed for one line's product (the deterministic-asin
   * "I verified this match"). Returns ok/error; mark_match_reviewed RAISES
   * (not a userErrors envelope) on its guards — 22023 when the product has no
   * asin (a fuzzy row must be promoted in ProductDetail first), 42501 for
   * viewer/dist_rep — so the PostgrestError message surfaces via `error`.
   */
  markMatchReviewed: (productId: string) => Promise<{ ok: boolean; error?: string }>;
  submitting: boolean;
  error: string | null;
  setError: (err: string | null) => void;
  toast: ToastState | null;
  dismissToast: () => void;
}

/**
 * PO Builder — handles PO creation, draft saving, credit checks.
 */
export function usePOBuilder(): UsePOBuilderResult {
  const { user } = useApp();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  // ── Generate PO number via RPC ──────────────────────────────
  const generatePONumber = useCallback(async (): Promise<string | null> => {
    const { data, error } = await callRpc('generate_po_number');
    if (error) {
      console.error('[usePOBuilder] generate_po_number error:', error.message);
      return null;
    }
    return data == null ? null : String(data);
  }, []);

  // ── Credit check ────────────────────────────────────────────
  const getCreditInfo = useCallback(async (distributorId: string): Promise<CreditInfo | null> => {
    // Batch-2 §C — source the displayed available-credit from get_credit_summary
    // (credit_limit − status-derived soft_reserve). Since R1-HF1 (2026-06-10)
    // both the display AND the approve guard (approve_purchase_order, T2.1)
    // read public.compute_soft_reserve() — ONE shared implementation — so the
    // on-screen number matches what actually blocks at approve by construction
    // (pre-HF1 the guard embedded a drifted copy of the formula — R1-F1).
    // distributors.credit_used is a STALE column written by nothing (see BACKLOG);
    // do NOT read it here.
    const { data, error } = await callRpc('get_credit_summary', {
      p_distributor_id: distributorId,
    });
    if (error || !data) return null;
    const row = data as { credit_limit: number; soft_reserve: number; available: number };
    return {
      creditLimit: Number(row.credit_limit) || 0,
      creditUsed: Number(row.soft_reserve) || 0, // the live, status-derived reserve (not the stale column)
      available: Number(row.available) || 0,
    };
  }, []);

  // ── Submit PO ───────────────────────────────────────────────
  const submitPO = useCallback(
    async ({
      poNumber,
      distributorId,
      orderDate,
      expectedDeliveryDate,
      fulfillmentPath,
      notes,
      lineItems,
    }: SubmitPOArgs): Promise<SubmitPOResult> => {
      if (!user) return { success: false, error: 'Not authenticated' };
      setSubmitting(true);
      setError(null);

      try {
        // T2.1 — one transactional RPC writes header + line_items + allocations
        // atomically, so an interrupted submit leaves NO orphan PO header. The
        // RPC computes the totals (qty*cost / qty*profit). The PO note stays a
        // separate non-fatal post-commit call below (it was always non-fatal).
        const pLineItems = lineItems.map((li) => ({
          product_id: li.product_id,
          isg_sku: li.isg_sku,
          quantity: li.quantity,
          cost_per_unit: li.cost_per_unit,
          fulfillment_path: li.fulfillment_path,
          est_profit_per_unit: li.est_profit_per_unit ?? null,
          profitability_snapshot: li.profitability_snapshot ?? null,
          allocations: li.allocations ?? {},
        }));

        const env = extractEnvelope<PurchaseOrderInsertResult>(
          await callRpc('submit_purchase_order', {
            p_po_number: poNumber,
            p_distributor_id: distributorId,
            p_order_date: orderDate,
            p_expected_delivery_date: expectedDeliveryDate || null,
            p_fulfillment_path: fulfillmentPath,
            p_line_items: pLineItems,
          })
        );

        if (!env.isOk || !env.data) {
          const msg = env.userErrors[0]?.message ?? 'Failed to submit PO';
          console.error('[usePOBuilder] submit_purchase_order failed:', msg);
          setError(msg);
          return { success: false, error: msg };
        }
        const poRow = env.data;

        // Day 53d: route PO notes to entity_notes per Day 48.5 design. Non-fatal
        // + post-commit — the money-critical writes are already atomic in the
        // RPC; a note failure logs + continues (user can re-add it later).
        if (notes && notes.trim().length > 0) {
          const noteEnv = extractEnvelope<{ id: string }>(
            await callRpc('create_entity_note', {
              p_entity_type: 'purchase_order',
              p_entity_id: poRow.id,
              p_text: notes.trim(),
              p_visibility: 'all_internal',
            })
          );
          if (!noteEnv.isOk) {
            console.error(
              '[usePOBuilder] create_entity_note failed:',
              noteEnv.userErrors.map((e) => e.message).join('; ')
            );
          }
        }

        // Server echo, never the client arg — the committed po_number is the
        // only identity the toast may announce (U10b S-C3; resubmit already
        // does this).
        setToast({ kind: 'success', message: `PO ${poRow.po_number} submitted` });
        return { success: true, po: poRow };
      } catch (e) {
        const msg = errorMessage(e);
        console.error('[usePOBuilder] submitPO error:', msg, e);
        setError(msg);
        return { success: false, error: msg };
      } finally {
        setSubmitting(false);
      }
    },
    [user]
  );

  // ── Save Draft ──────────────────────────────────────────────
  const saveDraft = useCallback(
    async ({
      poNumber,
      distributorId,
      orderDate,
      expectedDeliveryDate,
      fulfillmentPath,
      // notes intentionally dropped from draft payload (Day 53d) — see comment
      // below the insert call.
      lineItems,
    }: SubmitPOArgs): Promise<SubmitPOResult> => {
      if (!user) return { success: false, error: 'Not authenticated' };
      setSubmitting(true);
      setError(null);

      try {
        // Cluster 3a (FIX Session 2) — ONE transactional RPC writes the header
        // (status=draft) + line_items + allocations atomically, so an interrupted
        // save leaves NO orphan draft PO (was 3 sequential client writes). The RPC
        // computes the totals. Drafts still drop the PO note (Day 53d; entity_notes
        // for drafts deferred to the NotesPanel surface).
        const pLineItems = lineItems.map((li) => ({
          product_id: li.product_id,
          isg_sku: li.isg_sku,
          quantity: li.quantity,
          cost_per_unit: li.cost_per_unit,
          fulfillment_path: li.fulfillment_path,
          est_profit_per_unit: li.est_profit_per_unit ?? null,
          profitability_snapshot: li.profitability_snapshot ?? null,
          allocations: li.allocations ?? {},
        }));

        const env = extractEnvelope<PurchaseOrderInsertResult>(
          await callRpc('save_po_draft', {
            p_po_number: poNumber,
            p_distributor_id: distributorId,
            p_order_date: orderDate,
            p_expected_delivery_date: expectedDeliveryDate || null,
            p_fulfillment_path: fulfillmentPath,
            p_line_items: pLineItems,
          })
        );
        if (!env.isOk || !env.data) {
          const msg = env.userErrors[0]?.message ?? 'Failed to save draft';
          console.error('[usePOBuilder] save_po_draft failed:', msg);
          setError(msg);
          return { success: false, error: msg };
        }

        setToast({ kind: 'success', message: 'Draft saved' });
        return { success: true, po: env.data };
      } catch (e) {
        const msg = errorMessage(e);
        console.error('[usePOBuilder] saveDraft error:', msg, e);
        setError(msg);
        return { success: false, error: msg };
      } finally {
        setSubmitting(false);
      }
    },
    [user]
  );

  // ── W4b-α — atomic edit-mode resubmit ───────────────────────
  const resubmitPO = useCallback(
    async ({
      poId,
      distributorId,
      orderDate,
      expectedDeliveryDate,
      fulfillmentPath,
      lineItems,
    }: ResubmitPOArgs): Promise<SubmitPOResult> => {
      if (!user) return { success: false, error: 'Not authenticated' };
      setSubmitting(true);
      setError(null);

      try {
        // Same line-item shape as submitPO/saveDraft (the resubmit RPC reuses save_po_draft's
        // jsonb-loop: 'quantity' key + nested per-line 'allocations'). Totals are computed server-side.
        const pLineItems = lineItems.map((li) => ({
          product_id: li.product_id,
          isg_sku: li.isg_sku,
          quantity: li.quantity,
          cost_per_unit: li.cost_per_unit,
          fulfillment_path: li.fulfillment_path,
          est_profit_per_unit: li.est_profit_per_unit ?? null,
          profitability_snapshot: li.profitability_snapshot ?? null,
          allocations: li.allocations ?? {},
        }));

        const env = extractEnvelope<PurchaseOrderInsertResult>(
          await callRpc('resubmit_purchase_order', {
            p_po_id: poId,
            p_line_items: pLineItems,
            p_metadata: {
              distributor_id: distributorId,
              order_date: orderDate,
              expected_delivery_date: expectedDeliveryDate || null,
              fulfillment_path: fulfillmentPath,
            },
          })
        );

        if (!env.isOk || !env.data) {
          // PO.UNREVIEWED_MATCH / PO.NOT_EDITABLE / PO.NO_LINE_ITEMS surface here — same path submitPO uses.
          const msg = env.userErrors[0]?.message ?? 'Failed to resubmit PO';
          console.error('[usePOBuilder] resubmit_purchase_order failed:', msg);
          setError(msg);
          return { success: false, error: msg };
        }

        setToast({ kind: 'success', message: `PO ${env.data.po_number} resubmitted` });
        return { success: true, po: env.data };
      } catch (e) {
        const msg = errorMessage(e);
        console.error('[usePOBuilder] resubmitPO error:', msg, e);
        setError(msg);
        return { success: false, error: msg };
      } finally {
        setSubmitting(false);
      }
    },
    [user]
  );

  // ── W4a — mark one line's match reviewed (Moment 2 ack) ─────
  const markMatchReviewed = useCallback(
    async (productId: string): Promise<{ ok: boolean; error?: string }> => {
      const { error } = await callRpc('mark_match_reviewed', { p_product_id: productId });
      if (error) return { ok: false, error: error.message };
      return { ok: true };
    },
    []
  );

  // ── Dismiss toast ───────────────────────────────────────────
  const dismissToast = useCallback((): void => setToast(null), []);

  return {
    submitPO,
    saveDraft,
    resubmitPO,
    generatePONumber,
    getCreditInfo,
    markMatchReviewed,
    submitting,
    error,
    setError,
    toast,
    dismissToast,
  };
}
